import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, BadgeCheck, Loader2, RefreshCw, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { SignInRequired } from "@/components/SignInRequired";
import { useAuth } from "@/hooks/useAuth";
import { trialProgress } from "@/lib/premium";
import { accessExplanation, entitlementSummary, PREMIUM_ONLY_FEATURES, type GatedFeature } from "@/lib/entitlements";
import { trackEntitlementState } from "@/lib/analytics";
import {
  checkBillingBridgeHealth,
  isNativeStoreApp,
  storeLabel,
  STORE_PRODUCT_IDS,
  type BillingBridgeHealth,
} from "@/lib/store-billing";
import { storeProductInfo, storeIntroOfferEligibility } from "@/lib/native-billing";
import { bridgeCallLog, nativeBillingPluginPresent, type BridgeCallLog } from "@/lib/native-bridge";
import { useTranslation } from "react-i18next";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/subscription-status")({
  component: SubscriptionStatusPage,
  head: () => ({
    meta: [
      { title: "Subscription & Trial Status — Inquira" },
      {
        name: "description",
        content:
          "Diagnostics for your Inquira Premium entitlement: store-verified expiry date, trial state, feature access and last sync time.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Subscription & Trial Status — Inquira" },
      {
        property: "og:description",
        content: "Verified store expiry, entitlement state and last sync time for on-device debugging.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Subscription & Trial Status — Inquira" },
      { name: "twitter:description", content: "Entitlement diagnostics for real-device testing." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const fmt = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

interface StoreSnapshot {
  displayPrice?: string;
  isFreeTrial?: boolean;
  periodCount?: number;
  periodUnit?: string;
  storeExpiresAt?: string | null;
  eligible?: boolean | null;
  available: boolean;
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-rule/60 py-2 last:border-0">
      <span className="text-xs text-ink-muted">{label}</span>
      <span className={`text-right text-xs font-semibold text-ink ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function SubscriptionStatusPage() {
  const { user, isPaid, premium, scanCount, questionUsage, subscriptionStatus, refreshPremium, refreshUsage, signOut } = useAuth();
  const { t } = useTranslation();
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [store, setStore] = useState<StoreSnapshot | null>(null);
  const [log, setLog] = useState([] as BridgeCallLog[]);
  const [bridgeHealth, setBridgeHealth] = useState(null as BillingBridgeHealth | null);
  const native = isNativeStoreApp();
  const pluginPresent = nativeBillingPluginPresent();


  const readStore = useCallback(async () => {
    // Runtime plugin health first: it explains an empty snapshot below.
    setBridgeHealth(await checkBillingBridgeHealth());
    if (!isNativeStoreApp()) {
      setStore({ available: false });
      return;
    }
    const [info, eligible] = await Promise.all([
      storeProductInfo("monthly"),
      storeIntroOfferEligibility("monthly"),
    ]);
    setStore({
      available: Boolean(info),
      displayPrice: info?.displayPrice,
      isFreeTrial: info?.introOffer?.isFreeTrial,
      periodCount: info?.introOffer?.periodCount,
      periodUnit: info?.introOffer?.periodUnit,
      storeExpiresAt: info?.storeExpiresAt ?? null,
      eligible,
    });
    setLog(bridgeCallLog());
  }, []);

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      await Promise.all([refreshPremium(), refreshUsage()]);
      await readStore();
      setLastSync(new Date().toISOString());
    } finally {
      setSyncing(false);
    }
  }, [readStore, refreshPremium, refreshUsage]);

  useEffect(() => {
    void sync();
  }, [sync]);

  const liveSummary = entitlementSummary({ premium, scanCount, questionUsage });

  // Emit entitlement + gating telemetry whenever the resolved state changes.
  useEffect(() => {
    if (!premium) return;
    trackEntitlementState(
      {
        tier: liveSummary.tier,
        premium_access: liveSummary.premiumAccess,
        trial_status: premium.trial_status,
        store_platform: premium.store_platform,
        store_status: premium.store_status,
        store_expires_at: premium.store_expires_at,
        scans_remaining: Number.isFinite(liveSummary.scansRemaining) ? liveSummary.scansRemaining : null,
        scan_access: liveSummary.access.scan,
        solve_access: liveSummary.access.solve,
      },
      "subscription_status_screen",
    );
  }, [premium, liveSummary.tier, liveSummary.premiumAccess, liveSummary.scansRemaining, liveSummary.access.scan, liveSummary.access.solve]);

  if (!user) {
    return (
      <SignInRequired
        title={i18n.t("edu.subscription_status.subscription_trial_status")}
        description="Sign in to inspect your entitlement, store-verified expiry and sync state."
      />
    );
  }

  const trial = premium ? trialProgress(premium) : null;
  const summary = liveSummary;
  const verifiedExpiry = premium?.store_expires_at ?? store?.storeExpiresAt ?? null;

  return (
    <div className="min-h-screen bg-surface">
      <AppHeader user={user} isPaid={isPaid} onSignOut={() => void signOut()} />

      <main className="mx-auto w-full max-w-2xl space-y-4 px-4 py-6">
        <Link to="/subscription" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="size-4" /> <T k="edu.subscription_status.subscription" />
        </Link>

        <div className="flex items-center justify-between gap-3">
          <h1 className="font-[family-name:var(--font-display)] text-2xl font-extrabold text-ink">
            Subscription &amp; Trial Status
          </h1>
          <Button size="sm" onClick={() => void sync()} disabled={syncing}>
            {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            {syncing ? "Refreshing…" : "Refresh subscription status"}
          </Button>
        </div>
        <p className="text-xs text-ink-muted">
          Diagnostics for real-device testing. Every value below is read from the server or from
          Apple/Google — nothing here is inferred locally.
        </p>

        <section className="rounded-2xl border border-rule bg-card p-5">
          <h2 className="mb-2 text-sm font-bold text-ink"><T k="edu.subscription_status.entitlement" /></h2>
          <Row label={i18n.t("edu.subscription_status.tier")} value={summary.tier} />
          <Row label={i18n.t("edu.subscription_status.premium_access")} value={summary.premiumAccess ? "granted" : "not granted"} />
          <Row label={i18n.t("edu.subscription_status.subscription_status")} value={subscriptionStatus ?? "—"} />
          <Row label={i18n.t("edu.subscription_status.trial_status")} value={premium?.trial_status ?? "—"} />
          <Row
            label={i18n.t("edu.subscription_status.trial_countdown")}
            value={trial?.active ? `Day ${trial.dayNumber}/${trial.daysTotal} · ${trial.label}` : "—"}
          />
          <Row label={i18n.t("edu.subscription_status.trial_used")} value={premium?.trial_used ? "yes" : "no"} />
          <Row label={t("monetization.ai_questions_remaining_today")} value={Number.isFinite(summary.scansRemaining) ? String(summary.scansRemaining) : t("monetization.premium_benefits_preserved")} />
        </section>

        <section className="rounded-2xl border border-rule bg-card p-5">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-bold text-ink">
            <BadgeCheck className="size-4 text-neon-pink" /> <T k="edu.subscription_status.store_verified_dates" />
          </h2>
          <Row label={i18n.t("edu.subscription_status.billing_platform")} value={storeLabel(premium?.billing_platform as never) } />
          <Row label={i18n.t("edu.subscription_status.store_platform")} value={premium?.store_platform ?? "—"} />
          <Row label={i18n.t("edu.subscription_status.store_status")} value={premium?.store_status ?? "—"} />
          <Row label={i18n.t("edu.subscription_status.verified_expiresdate")} value={fmt(verifiedExpiry)} mono />
          <Row label={i18n.t("edu.subscription_status.grace_period_ends")} value={fmt(premium?.grace_period_ends_at)} mono />
          <Row label={i18n.t("edu.subscription_status.current_period_end")} value={fmt(premium?.current_period_end)} mono />
          <Row label={i18n.t("edu.subscription_status.trial_ends_server")} value={fmt(premium?.trial_ends_at)} mono />
          <Row label={i18n.t("edu.subscription_status.last_sync")} value={fmt(lastSync)} mono />
        </section>

        <section className="rounded-2xl border border-rule bg-card p-5">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-bold text-ink">
            <Smartphone className="size-4 text-neon-yellow" /> <T k="edu.subscription_status.native_store_bridge" />
          </h2>
          <Row label={i18n.t("edu.subscription_status.wrapper_detected")} value={native ? "yes" : "no (web)"} />
          <Row
            label={i18n.t("edu.subscription_status.inquirabilling_plugin")}
            value={pluginPresent ? "✓ registered in this build" : "✕ not registered"}
          />
          <Row
            label={i18n.t("edu.subscription_status.storekit_product_load")}
            value={store === null ? "checking…" : store.available ? "✓ product returned" : "✕ no product returned"}
          />
          <Row
            label={i18n.t("edu.subscription_status.apple_verified_expiry")}
            value={verifiedExpiry ? "✓ present" : "✕ none — no verified store purchase"}
          />
          <Row label={t("adminScreens.bridgeHealth")} value={bridgeHealth?.state ?? "checking…"} mono />

          <Row
            label={t("adminScreens.webCheckoutFallback")}
            value={bridgeHealth ? (bridgeHealth.webFallbackAvailable ? "available" : "not allowed (iOS)") : "—"}
          />
          {bridgeHealth && bridgeHealth.state !== "ok" && bridgeHealth.state !== "web" && (
            <p className="mt-2 rounded-lg border border-rule bg-background/60 p-2 text-[11px] leading-snug text-ink-muted">
              {bridgeHealth.message}
            </p>
          )}
          <Row label={i18n.t("edu.subscription_status.product_id")} value={STORE_PRODUCT_IDS.monthly} mono />
          <Row label={i18n.t("edu.subscription_status.localized_price")} value={store?.displayPrice ?? "—"} />
          <Row
            label={i18n.t("edu.subscription_status.intro_offer")}
            value={
              store?.isFreeTrial
                ? `${store.periodCount ?? 3}-${store.periodUnit ?? "day"} free trial`
                : store?.available
                  ? "none reported"
                  : "—"
            }
          />
          <Row
            label={i18n.t("edu.subscription_status.apple_google_eligibility")}
            value={store?.eligible === null || store?.eligible === undefined ? "unknown" : store.eligible ? "eligible" : "not eligible"}
          />
          {log.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-xs font-bold text-ink"><T k="edu.subscription_status.recent_bridge_calls" /></p>
              {log.map((entry, i) => (
                <p key={`${entry.at}-${i}`} className="truncate font-mono text-[11px] text-ink-muted">
                  {entry.ok ? "✓" : "✕"} {entry.method} · {entry.detail}
                </p>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-rule bg-card p-5">
          <h2 className="mb-2 text-sm font-bold text-ink"><T k="edu.subscription_status.feature_access" /></h2>
          <Row label={i18n.t("edu.subscription_status.scan_solve")} value={summary.access.scan ? "allowed" : "blocked"} />
          {PREMIUM_ONLY_FEATURES.map((feature: GatedFeature) => (
            <Row
              key={feature}
              label={feature.charAt(0).toUpperCase() + feature.slice(1)}
              value={summary.access[feature] ? "allowed" : "Premium required"}
            />
          ))}
          <div className="mt-4 space-y-2 border-t border-rule/60 pt-3">
            <p className="text-xs font-bold text-ink"><T k="edu.subscription_status.why_each_feature_is_locked_or_unlocked" /></p>
            {(["scan", ...PREMIUM_ONLY_FEATURES] as GatedFeature[]).map((feature) => (
              <div key={`why-${feature}`} className="flex items-start gap-2">
                <span
                  className={`mt-1 size-1.5 shrink-0 rounded-full ${summary.access[feature] ? "bg-neon-yellow" : "bg-ink-muted"}`}
                  aria-hidden
                />
                <p className="text-[11px] leading-relaxed text-ink-muted">
                  <span className="font-semibold text-ink">
                    {feature === "scan" ? "Scan / Solve" : feature.charAt(0).toUpperCase() + feature.slice(1)}:
                  </span>{" "}
                  {accessExplanation(feature, summary, premium)}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}